import React, { useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { ThunkDispatch } from 'redux-thunk';
import type { AnyAction } from 'redux';
import styles from './asset-browser-search.module.scss';
import { ForgeAutocomplete, ForgeIcon, ForgeIconButton, ForgeTextField } from '@tylertech/forge-react';
import { IAutocompleteOption } from '@tylertech/forge';
// TODO EN-86773: import { I18n } from 'common/i18n';
import airbrake from 'common/airbrake';
import ceteraUtils from 'common/cetera/utils';
import { changeQ, clearSearch } from './actions/filters';

// TODO EN-86773: Translations
// const scope = 'shared.asset_browser';

export const AssetBrowserSearch = () => {
  const dispatch = useDispatch<ThunkDispatch<any, void, AnyAction>>();
  const searchQuery = useSelector((state: any) => state.filters.q || '');
  const inputRef = useRef<HTMLInputElement>(null);

  const fetchAssetListForAutocomplete = useCallback(
    (assetSearch: string): Promise<IAutocompleteOption<string>[]> => {
      if (assetSearch === '') {
        return Promise.resolve([]);
      }

      return ceteraUtils
        .autocompleteQuery(assetSearch)
        .then((response: any) => {
          return response.results;
        })
        .then((assetResults: any[]) => {
          const resultTitles: IAutocompleteOption<string>[] = [];
          assetResults.forEach((res) => {
            const newResult: IAutocompleteOption<string> = {
              label: res.title,
              value: res.title,
            };
            resultTitles.push(newResult);
          });
          return resultTitles;
        })
        .catch((err: any) => {
          airbrake.notify({
            err,
            context: { component: 'AssetBrowserSearch' },
          });

          return [];
        });
    },
    [],
  );

  const handleSearch = useCallback(
    (value: string) => {
      if (value.trim()) {
        dispatch(changeQ(value.trim()));
      }
    },
    [dispatch],
  );

  const handleAutocompleteSelect = useCallback(
    (event: CustomEvent) => {
      const value = event.detail.value;
      handleSearch(value);
    },
    [handleSearch],
  );

  const handleClearSearch = useCallback(() => {
    dispatch(clearSearch());
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  }, [dispatch]);

  const handleKeyUp = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        const value = (event.target as HTMLInputElement).value;
        handleSearch(value);
      }
    },
    [handleSearch],
  );

  return (
    <ForgeAutocomplete
      filter={fetchAssetListForAutocomplete}
      on-forge-autocomplete-select={handleAutocompleteSelect}
      filterOnFocus={false}
      filterFocusFirst={false}
      allowUnmatched={true}
      mode="stateless"
      popupTarget="asset-search-autocomplete-results"
      id="asset-search-autocomplete"
    >
      <ForgeTextField shape="rounded" variant="filled" className={styles['search-bar']}>
        <ForgeIcon slot="start" name="search" />
        <input
          ref={inputRef}
          // TODO EN-86773: placeholder={I18n.t('search.placeholder', { scope })}
          placeholder="Search assets..."
          data-testid="asset-search-autocomplete-input"
          type="text"
          aria-label="Search assets..."
          // TODO EN-86773: aria-label={I18n.t('search.placeholder', { scope })}
          aria-controls="asset-search-autocomplete-results"
          onKeyUp={handleKeyUp}
          defaultValue={searchQuery}
        />
        {searchQuery && (
          <ForgeIconButton
            data-forge-autocomplete-clear
            slot="end"
            density="3"
            aria-label="Clear all"
            // TODO EN-86773: aria-label={I18n.t('search.search_clear', { scope })}
            onClick={handleClearSearch}
          >
            <ForgeIcon name="close"></ForgeIcon>
          </ForgeIconButton>
        )}
      </ForgeTextField>
      <div id="asset-search-autocomplete-results" />
    </ForgeAutocomplete>
  );
};
